import * as React from "react";
import {ProcessStage, ProcessStageArrow, ProcessStageArrowDirection, ProcessStageOrder} from "../../Processes";
import {Button, Notification, Spinner, SpinnerSize, TextInput} from "@vebox/etui-core";
import {INPUT_BORDER_BOX} from "../../../components/common/etui_components_styles";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {LoadingButton} from "../../../components/buttons/LoadingButton";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {VeboxOverlayController} from "../../../components/overlay/VeboxOverlay";
import {VeboxModels} from "../../../types/veboxModels";
import {defaultTheme} from "@vebox/etui-themes";
import {getPackages, PackagesContext} from "./PackagesProvider";

import "./ClaimAndCheck.scss";
import {Display} from "../../../components/Display";
import PackageStatus = VeboxModels.PackageStatus;

declare var AppTranslations: AppTranslations;

interface ClaimAndCheckProps {
    overlayController: VeboxOverlayController;
}

const packageStatusCountReducer = (status: VeboxModels.PackageStatus) => (notClaimableCount: number,
                                                                          p: VeboxModels.Package) => notClaimableCount + (p.status === status ? 1 : 0);

const notClaimableCountReducer = packageStatusCountReducer(VeboxModels.PackageStatus.NOT_CLAIMABLE);

const noFoundCountReducer = packageStatusCountReducer(VeboxModels.PackageStatus.NOT_FOUND);

const claimableCountReducer = packageStatusCountReducer(VeboxModels.PackageStatus.CLAIMABLE);

const claimableDevicesCountReducer = (claimableDeviceCount: number,
                                      p: VeboxModels.Package) => claimableDeviceCount + p.deviceQuantity * (p.status === VeboxModels.PackageStatus.CLAIMABLE ? 1 : 0);

export const ClaimAndCheck: React.FC<ClaimAndCheckProps> = (props) => {
    const packageClaimTranslations = AppTranslations.massiveActions.packageClaimProcess;

    const {packages, setPackages, movePackages, fetchPackages} = React.useContext(PackagesContext);
    const [loading, setLoading] = React.useState<boolean>(false);

    const [packageCodeFilter, setPackageCodeFilter] = React.useState<string>("");

    const changePackageCodeFilter = e => setPackageCodeFilter(e.target.value);

    const packagesNotFound: number = packages.reduce(noFoundCountReducer, 0);
    const packagesNotClaimable: number = packages.reduce(notClaimableCountReducer, 0);
    const packagesClaimable: number = packages.reduce(claimableCountReducer, 0);
    const numberOfDevices: number = packages.reduce(claimableDevicesCountReducer, 0);

    const removeNotClaimable = () => {
        setPackages(packages.filter(p => p.status === VeboxModels.PackageStatus.CLAIMABLE));
    }

    const removePackage = (packageCode: string) => {
        setPackages(packages.filter(p => p.code !== packageCode));
    }

    const periodicFetchPackages = (codes: string[]) => {
        return new Promise<void>(res => {
            const intervalID = setInterval(() => {
                getPackages(codes)
                    .then(updatedPackages => {
                        setPackages(updatedPackages.map(up => ({
                            ...up,
                            // status: codes.findIndex(c => c === up.code) > -1 && up.status === VeboxModels.PackageStatus.CLAIMABLE ? VeboxModels.PackageStatus.BEING_CLAIMED : up.status,
                        })));
                        return updatedPackages
                    })
                    .then(updatedPackages => {
                        if (updatedPackages.filter(
                            p => p.status === VeboxModels.PackageStatus.BEING_CLAIMED).length === 0) {
                            clearInterval(intervalID);
                            res();
                        }
                    })
            }, 10 * 1000); // Clear the interval when the components gets destroyed
        })
    }

    const claimPackages = () => {
        setLoading(true);
        setPackages(
            packages.map(p => ({
                    ...p,
                    status: PackageStatus.BEING_CLAIMED,
                })
            ));
        movePackages(packages.map(p => p.code))
            .then(() => periodicFetchPackages(packages.map(p => p.code)))
            .then(() => {
                Notification.open({
                    type: "success",
                    message: packageClaimTranslations.successMessageTitle,
                    description: packageClaimTranslations.successMessageDescription,
                    duration: 2,
                    theme: defaultTheme,
                });
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                Notification.open({
                    type: "error",
                    message: packageClaimTranslations.errorMessageTitle,
                    description: packageClaimTranslations.errorMessageDescription,
                    duration: 5,
                    theme: defaultTheme,
                });
            });
    };

    const openModal = () => props.overlayController.show();

    return (
        <ProcessStage
            stepNumber={2}
            title={packageClaimTranslations.packageClaimStageTitle}
            stageOrder={ProcessStageOrder.LAST}
        >
            <div className={"flx-row"}>
                <ProcessStageArrow stepNumber={2} direction={ProcessStageArrowDirection.BACKWARD} visible={true}/>
                <div className={"codes-check-and-approve-box"}>
                    {((packagesNotFound + packagesNotClaimable) > 0) &&
                        <WarningBanner
                            numberNotClaimable={packagesNotClaimable}
                            numberNotFound={packagesNotFound}
                            onRemove={removeNotClaimable}
                        />
                    }
                    <VeBoxCard className={"form-card"} style={{width: 650, boxSizing: "border-box", gridRowStart: 2}}>
                        <div className={"flx-row"}>
                            <TextInput
                                label={packageClaimTranslations.packageCodeFilterPlaceholder}
                                styles={{...INPUT_BORDER_BOX, root: {width: "100%"}} as any}
                                autoComplete={"off"}
                                value={packageCodeFilter}
                                onChange={changePackageCodeFilter}
                            />
                            <Button onClick={openModal} variant={"tertiary"}>
                                <span className="material-icons-outlined">help_outline</span>
                            </Button>
                        </div>
                        <ul className={"claim-list"}>
                            {
                                [...packages]
                                    .sort(a => a.status === VeboxModels.PackageStatus.CLAIMABLE ? -1 : 1)
                                    .filter(p => p.code.includes(packageCodeFilter))
                                    .map((p, i) => (
                                        <ClaimListItem key={i} packageDetail={p} onRemove={removePackage}/>
                                    ))
                            }
                        </ul>
                        <div className={"flx-row flx-space-between"}>
                            <p>
                                <Display isVisible={packages?.length > 0}>

                                    {packagesClaimable} {packagesClaimable === 1 ? packageClaimTranslations.claimablePackagesCountLabel : packageClaimTranslations.claimablePackagesCountPluralLabel}, {numberOfDevices} {numberOfDevices === 1 ? packageClaimTranslations.devicesCountLabel : packageClaimTranslations.devicesCountPluralLabel}

                                </Display>
                            </p>
                            <LoadingButton
                                variant={"strong"}
                                onClick={claimPackages}
                                loading={loading}
                                disabled={packagesNotClaimable > 0 || packagesNotFound > 0 || packages.length === 0 || packagesClaimable === 0}
                            >
                                {packageClaimTranslations.claimPackageButton}
                            </LoadingButton>
                        </div>
                    </VeBoxCard>
                </div>
                <ProcessStageArrow stepNumber={2} direction={ProcessStageArrowDirection.FORWARD}/>
            </div>
        </ProcessStage>
    );
}

interface ClaimListItemProps {
    packageDetail: VeboxModels.Package;
    onRemove?: (packageCode: string) => void;
}

const ClaimListItem: React.FC<ClaimListItemProps> = ({packageDetail, onRemove}) => {
    const packageClaimTranslations = AppTranslations.massiveActions.packageClaimProcess;
    const removePackage = () => onRemove(packageDetail.code);
    switch (packageDetail.status) {
        case VeboxModels.PackageStatus.NOT_FOUND: {
            return (
                <li className={"flx-row flx-align-center flx-space-between claim-list-item not-found"}>
                    <div className={"flx-row flx-align-center"}>
                        <div className={"claim-list-item-icon"}>
                            <span className="material-symbols-outlined">priority_high</span>
                        </div>
                        <div className={"flx-col"}>
                            <h2 className={"bold"}>{packageDetail.code}</h2>
                            <p>{packageClaimTranslations.packageNotFound}</p>
                        </div>
                    </div>
                    <Button onClick={removePackage} variant={"tertiary"}>
                        <span className="material-symbols-outlined">delete</span>
                    </Button>
                </li>
            );
        }
        case VeboxModels.PackageStatus.NOT_CLAIMABLE: {
            return (
                <li className={"flx-row flx-align-center flx-space-between claim-list-item warning"}>
                    <div className={"flx-row flx-align-center"}>
                        <div className={"claim-list-item-icon"}>
                            <span className="material-symbols-outlined">warning</span>
                        </div>
                        <div className={"flx-col"}>
                            <h2 className={"bold"}>{packageDetail.code}</h2>
                            <p>{packageClaimTranslations.packageNotClaimable}</p>
                        </div>
                    </div>
                    <Button onClick={removePackage} variant={"tertiary"}>
                        <span className="material-symbols-outlined">delete</span>
                    </Button>
                </li>
            );
        }
        case VeboxModels.PackageStatus.CLAIMABLE: {
            return (
                <li className={"flx-row flx-align-center claim-list-item"}>
                    <div className={"claim-list-item-icon"}>
                        <img src={packageDetail?.deviceModel?.logo} alt={""} width={40} height={40}/>
                    </div>
                    <div className={"flx-col"}>
                        <h2 className={"bold"}>{packageDetail.code}</h2>
                        <p>{packageDetail.deviceQuantity}, {packageDetail?.deviceModel?.name}</p>
                    </div>
                </li>
            );
        }
        case VeboxModels.PackageStatus.BEING_CLAIMED: {
            return (
                <li className={"flx-row flx-align-center flx-space-between claim-list-item"}>
                    <div className={"flx-row flx-align-center"}>
                        <div className={"claim-list-item-icon"}>
                            <img src={packageDetail?.deviceModel?.logo} alt={""} width={40} height={40}/>
                        </div>
                        <div className={"flx-col"}>
                            <h2 className={"bold"}>{packageDetail.code}</h2>
                            <p>{packageDetail.deviceQuantity}, {packageDetail?.deviceModel?.name}</p>
                        </div>
                    </div>
                    <Spinner size={SpinnerSize.small} styles={{root: {p: 10}}}/>
                </li>
            );
        }
        case VeboxModels.PackageStatus.ALREADY_YOURS: {
            return (
                <li className={"flx-row flx-align-center flx-space-between claim-list-item"}>
                    <div className={"flx-row flx-align-center"}>
                        <div className={"claim-list-item-icon"}>
                            <img src={packageDetail.deviceModel.logo} alt={""} width={40} height={40}/>
                        </div>
                        <div className={"flx-col"}>
                            <h2 className={"bold"}>{packageDetail.code}</h2>
                            <p>{packageDetail.deviceQuantity}, {packageDetail.deviceModel.name}</p>
                        </div>
                    </div>
                    <div className={"package-claimed-check"}>
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                </li>
            );
        }
        default: {
            return (
                <li className={"claim-list-item"}> Error </li>
            );
        }
    }
}

interface WarningBannerProps {
    numberNotFound?: number;
    numberNotClaimable?: number;
    onRemove: () => void;
}

const WarningBanner: React.FC<WarningBannerProps> = ({numberNotFound, numberNotClaimable, onRemove}) => {
    const packageClaimTranslations = AppTranslations.massiveActions.packageClaimProcess;
    return (
        <VeBoxCard
            className={"warning-notification centered flx-row flx-space-between"}
            style={{width: 650, margin: "5px 0px"}}
        >
            <p className={"bold"}>
                {(numberNotFound > 0) && <> {numberNotFound} {numberNotFound === 1 ? packageClaimTranslations.packageLabel : packageClaimTranslations.packagesLabel}
                    <span className={"not-found"}> {numberNotFound === 1 ? packageClaimTranslations.notFoundLabel : packageClaimTranslations.notFoundPluralLabel} </span> </>}
                {((numberNotFound > 0) && (numberNotClaimable > 0)) && <> {packageClaimTranslations.and} </>}
                {(numberNotClaimable > 0) && <> {numberNotClaimable} {!numberNotFound && <> {numberNotClaimable === 1 ? packageClaimTranslations.packageLabel : packageClaimTranslations.packagesLabel} </>}
                    <span className={"not-claimable"}> {numberNotClaimable === 1 ? packageClaimTranslations.notClaimableLabel : packageClaimTranslations.notClaimablePluralLabel} </span></>}
            </p>
            <span className={"suggestion"} onClick={onRemove}> {packageClaimTranslations.removeThemFromList}</span>
        </VeBoxCard>
    )
}